
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ReviewUI } from "@/types/supabase";
import { useAuth } from "@/contexts/auth";
import ReviewListSkeleton from "./reviews/ReviewListSkeleton";
import ReviewFilters from "./reviews/ReviewFilters";
import ReviewItem from "./reviews/ReviewItem";

const REVIEWS_PER_PAGE = 5;
const AD_INSERTION_INDEX = 2; // After the 3rd review (0-indexed)

interface ReviewListProps {
  reviews: ReviewUI[];
  isLoading?: boolean;
  isPremiumProvider?: boolean;
  adsEnabled?: boolean;
}

const ReviewList = ({ reviews, isLoading = false, isPremiumProvider = false, adsEnabled = false }: ReviewListProps) => {
  const { user, isAdmin } = useAuth();
  const [sortBy, setSortBy] = useState<"recent" | "highest" | "lowest">("recent");
  const [filterContacted, setFilterContacted] = useState<boolean>(false);
  const [visibleCount, setVisibleCount] = useState(REVIEWS_PER_PAGE);
  
  const filteredReviews = filterContacted 
    ? reviews.filter(review => review.isContactedProvider)
    : reviews;
    
  const sortedReviews = [...filteredReviews].sort((a, b) => {
    if (sortBy === "recent") {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    } else if (sortBy === "highest") {
      return b.rating - a.rating;
    } else {
      return a.rating - b.rating;
    }
  });

  const reviewsToShow = sortedReviews.slice(0, visibleCount);

  if (isLoading) {
    return <ReviewListSkeleton />;
  }

  return (
    <section className="rounded-md border border-brand-cloud bg-white shadow-sm">
      <div className="border-b border-brand-cloud p-5 md:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="public-section-title">
            Reviews {reviews.length > 0 && `(${reviews.length})`}
          </h2>
          <ReviewFilters
            sortBy={sortBy}
            setSortBy={setSortBy}
            filterContacted={filterContacted}
            setFilterContacted={setFilterContacted}
          />
        </div>
      </div>
      
      <div className="p-5 md:p-6">
        {sortedReviews.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-brand-slate">No reviews yet. Be the first to leave a review.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-brand-cloud">
              {reviewsToShow.map((review, index) => (
                <ReviewItem
                  key={review.id}
                  review={review}
                  user={user}
                  isAdmin={isAdmin}
                  isPremiumProvider={isPremiumProvider}
                  adsEnabled={adsEnabled}
                  showAd={adsEnabled && !isPremiumProvider && index === AD_INSERTION_INDEX && sortedReviews.length > AD_INSERTION_INDEX + 1}
                />
              ))}
            </div>
            {sortedReviews.length > visibleCount && (
              <div className="text-center mt-8">
                <Button 
                  onClick={() => setVisibleCount(prev => prev + REVIEWS_PER_PAGE)}
                  variant="outline"
                  className="rounded-md border-brand-cloud text-brand-ink hover:bg-brand-mist hover:text-brand-ink"
                >
                  Load More Reviews
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ReviewList;
